import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const HeaderContainer = styled.header`
  background: ${props => props.theme.colors.surface};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  padding: 0;
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(8px);
  background: rgba(255, 255, 255, 0.95);
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 72px;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: ${props => props.theme.colors.primary};
  font-weight: 700;
  font-size: 20px;
  
  .pyramid {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 16px;
    
    &::before {
      content: '🔺';
      filter: grayscale(1) contrast(2);
    }
  }
  
  .text {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    
    .main {
      font-size: 18px;
      font-weight: 700;
    }
    
    .sub {
      font-size: 11px;
      font-weight: 400;
      color: ${props => props.theme.colors.textLight};
      text-transform: uppercase;
      letter-spacing: 1px;
    }
  }
  
  &:hover .pyramid {
    transform: scale(1.05);
    transition: transform 0.2s ease;
  }
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${props => props.theme.colors.surface};
    border-top: 1px solid ${props => props.theme.colors.border};
    padding: 12px;
    gap: 4px;
    justify-content: space-around;
    backdrop-filter: blur(8px);
    background: rgba(255, 255, 255, 0.95);
  }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  text-decoration: none;
  color: ${props => props.theme.colors.textLight};
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  position: relative;
  
  &.active {
    color: ${props => props.theme.colors.primary};
    background: rgba(26, 26, 26, 0.06);
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 4px;
      height: 4px;
      background: ${props => props.theme.colors.secondary};
      border-radius: 50%;
    }
  }
  
  &:hover:not(.active) {
    color: ${props => props.theme.colors.text};
    background: rgba(26, 26, 26, 0.03);
  }
  
  .icon {
    font-size: 18px;
    display: flex;
    align-items: center;
  }
  
  .label {
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
    padding: 8px 12px;
    font-size: 10px;
    
    .icon {
      font-size: 20px;
    }
    
    .label {
      display: block;
      font-size: 10px;
    }
    
    &::after {
      display: none;
    }
    
    &.active {
      background: transparent;
      
      .icon {
        color: ${props => props.theme.colors.secondary};
      }
    }
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 16px;
  font-size: 12px;
  color: #4CAF50;
  font-weight: 500;
  
  .dot {
    width: 6px;
    height: 6px;
    background: #4CAF50;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(26, 26, 26, 0.03);
  }
  
  .avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${props => props.theme.colors.secondary};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    color: white;
    font-size: 14px;
  }
  
  .info {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    
    .name {
      font-size: 14px;
      font-weight: 600;
      color: ${props => props.theme.colors.primary};
    }
    
    .target {
      font-size: 11px;
      color: ${props => props.theme.colors.textLight};
    }
  }
`;

const MobileHeader = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .status-mobile {
      font-size: 10px;
      padding: 4px 8px;
    }
  }
`;

const Header = () => {
  const location = useLocation();
  
  const navItems = [
    { path: '/', label: 'Dashboard', icon: '📊' },
    { path: '/nutrition', label: 'Nutrition', icon: '🍽️' },
    { path: '/workout', label: 'Workout', icon: '💪' },
    { path: '/analytics', label: 'Analytics', icon: '📈' },
    { path: '/profile', label: 'Profile', icon: '⚙️' }
  ];

  return (
    <>
      <HeaderContainer>
        <HeaderContent>
          <Logo to="/">
            <div className="pyramid"></div>
            <div className="text">
              <div className="main">PYRAMID</div>
              <div className="sub">HEALTH</div>
            </div>
          </Logo>

          <Navigation>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
              >
                <span className="icon">{item.icon}</span>
                <span className="label">{item.label}</span>
              </NavLink>
            ))}
          </Navigation>

          <UserSection>
            <StatusIndicator>
              <div className="dot"></div>
              Online
            </StatusIndicator>
            
            <UserProfile>
              <div className="avatar">R</div>
              <div className="info">
                <div className="name">Robert</div>
                <div className="target">Maintenance Phase</div>
              </div>
            </UserProfile>
          </UserSection>

          <MobileHeader>
            <StatusIndicator className="status-mobile">
              <div className="dot"></div>
              Online
            </StatusIndicator>
          </MobileHeader>
        </HeaderContent>
      </HeaderContainer>
      
      {/* Mobile navigation spacer */}
      <div style={{ height: '80px', display: 'none' }} className="mobile-spacer">
        <style jsx>{`
          @media (max-width: 768px) {
            .mobile-spacer {
              display: block !important;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default Header;